/**
 * Shared logic for the spec-derived (EDC/SDTM) reupload + amend flows, where an uploaded spec is
 * parsed into candidate forms and one is pre-selected to match the DTA's current domain. Extracted
 * from the DataStreamDetails page and AmendModal so the selection rule is defined once and
 * unit-tested directly.
 */

import type { ParsedFormResult } from "@/generated/sdk"

/**
 * The subset of a parsed form the selection/domain rules need, tied to the generated contract.
 */
type ParsedForm = Pick<ParsedFormResult, "name" | "label">

/**
 * Source types whose reupload parses a spec into domain forms (vs. generic extraction).
 */
export function isSpecDerivedSource(sourceType: string | undefined): boolean {
  return sourceType === "edc" || sourceType === "sdtm"
}

/**
 * The domain a parsed form maps to: its label if present, else its name.
 */
export function formDomain(form: ParsedForm): string {
  return form.label || form.name
}

/**
 * Choose which parsed form to pre-select after a reupload.
 *
 * Prefers the form whose name or label matches the DTA's current domain; falls back to the first
 * form. Returns undefined when there are no forms.
 */
export function selectReuploadForm(
  forms: ParsedForm[] | undefined,
  currentDomain: string | undefined,
): string | undefined {
  if (!forms || forms.length === 0) return undefined
  if (currentDomain) {
    const match = forms.find((f) => f.name === currentDomain || f.label === currentDomain)
    if (match) return match.name
  }
  return forms[0].name
}
